import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalsContext } from '../../../../../../../../../providers/ModalsProvider/Modals.context.ts';
import { eAlertType } from '../../../../../../../../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../../../../../../../../providers/ToastProvider/Toast.hook.ts';
import { useWorkspaceContext } from '../../../../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import {
  buildOutboundRefusalAlert,
  lookupOutboundRefusal,
} from '../../../../../../../../../utils/ErrorDirectory.ts';
import { useChatContext } from '../../../../../../../Chat.hook.ts';
import { tChatEvent } from '../../../../../../../Chat.types.ts';
import useSendReaction from './useSendReaction.hook.ts';
import { useWapiPlanSendGuard } from 'src/components/Chat/useWapiPlanSendGuard.hook';
import { eChannelType } from 'src/providers/SocketsProvider/Sockets.enums';

/**
 * The business's reaction to one WhatsApp API message: shown at once, sent to
 * Meta, and rolled back if the send fails. An empty emoji, or the one already
 * there, removes it. Shared by the message bubble and each photo of an album
 * (INT-1227), since every photo is its own WhatsApp message.
 */
export default function useEventReaction(event: tChatEvent) {
  const { selectedChat, setChatEvents } = useChatContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const { currentWorkspace } = useWorkspaceContext();
  const { addAlert } = useToastContext();
  const { sendReaction } = useSendReaction();
  const { blockWapiPlanSend } = useWapiPlanSendGuard();
  // `errors/errors` holds the copy for a refused reaction.
  const { t } = useTranslation(['chat/chat', 'errors/errors']);
  const reactionSeqRef = useRef(0);

  return useCallback(
    async (emoji: string) => {
      if (
        !selectedChat ||
        !currentWorkspace?.id ||
        !event.providerId ||
        !selectedLeadForChatModal?.handle
      )
        return;
      // RET-2349: a reaction always goes out by WhatsApp API. Refused before
      // the optimistic write, so nothing has to be rolled back.
      if (blockWapiPlanSend(eChannelType.WHATSAPP_API)) return;

      // Snapshot before optimistic write to avoid stale closure in rollback
      const originalReactions = event.reactions ?? [];
      const previousMyReaction = originalReactions.find((r) => r.fromMe);
      const isRemoval = emoji === '' || emoji === previousMyReaction?.emoji;
      const optimisticEmoji = isRemoval ? '' : emoji;
      const seq = ++reactionSeqRef.current;

      setChatEvents((prev) => {
        const idx = prev.findIndex((evt) => evt.id === event.id);
        if (idx === -1) return prev;
        const target = prev[idx];
        const otherReactions = (target.reactions ?? []).filter(
          (r) => !r.fromMe,
        );
        const next = [...prev];
        next[idx] = {
          ...target,
          reactions: optimisticEmoji
            ? [
                ...otherReactions,
                {
                  id: 0,
                  chatEventId: event.id,
                  providerId: '',
                  fromMe: true,
                  emoji: optimisticEmoji,
                  createdBy: null,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                },
              ]
            : otherReactions,
        };
        return next;
      });

      try {
        await sendReaction({
          workspaceId: currentWorkspace.id,
          recipientPhone: selectedLeadForChatModal.handle,
          messageId: event.providerId,
          emoji: isRemoval ? '' : emoji,
        });
      } catch (error) {
        if (seq !== reactionSeqRef.current) return; // superseded by newer request
        setChatEvents((prev) => {
          const idx = prev.findIndex((evt) => evt.id === event.id);
          if (idx === -1) return prev;
          const target = prev[idx];
          const next = [...prev];
          next[idx] = { ...target, reactions: originalReactions };
          return next;
        });
        // A reaction is a send: while the plan is paused the server refuses it
        // (423), and the generic reaction-failed line would invite retries that
        // can only fail again.
        const refusal = lookupOutboundRefusal(error);
        addAlert(
          refusal
            ? buildOutboundRefusalAlert(refusal, t)
            : { type: eAlertType.ERROR, message: t('reactions.sendError') },
        );
      }
    },
    [
      selectedChat,
      currentWorkspace?.id,
      event.id,
      event.providerId,
      event.reactions,
      selectedLeadForChatModal?.handle,
      sendReaction,
      blockWapiPlanSend,
      setChatEvents,
      addAlert,
      t,
    ],
  );
}
